import {WorkflowTargetModel} from "../workflow/models/workflow-target.model";
import {UserModel, userModelListFactory} from "../types/user.model";
import {AddressModel, addressModelFactory} from "../address/address.model";
import {ContactModel, contactModelFactory} from "../contact/contact.model";
import {
  InstallationInstructions,
  installationInstructionsModelFactory,
} from "../installation-dialog/installation.model";
import {
  ProjectOffHoldIncrementModel,
  ProjectOffHoldIncrementModelFactory,
} from "../workflow/project/project-off-hold.model";

export interface ProjectModel extends WorkflowTargetModel {
  id?: string;
  name?: string;
  countryId?: string;
  country?: any;
  resellerBpId?: string;
  resellerName?: string;
  customerBpId?: string;
  customerName?: string;
  targetSystem?: string;
  uniqueId?: string;
  opportunityId?: string;
  status?: string;
  startDate?: string;
  customerKickoffDate?: string;
  estimatedProjectClosureDate?: string;
  actualProjectClosureDate?: string;
  estimatedTransitionDate?: number;
  actualTransitionDate?: number;
  estimatedCompletionDate?: string;
  firstInstallDate?: string;
  firstOrderDate?: string;
  totalCustomerValue?: number;
  currency?: string;
  users?: UserModel[];
  ampId?: string;
  csa?: string;
  msId?: string;
  orderAcceptance?: boolean;
  orderAcceptanceDisableReason?: string;
  orderAcceptanceDisableDate?: string;
  sourceDocumentationLink?: string;
  initialBillingDate?: number;
  dashboardMilestones?: any[];
  dashboardIssues?: any[];
  customerErpNumber?: string;
  handoverIdNumber?: string;
  address?: AddressModel;
  billToContact?: ContactModel;
  workflowId?: string;
  deployedDevices?: number;
  orderedDevices?: number;
  requestedOrdersCount?: number;
  requestedBulkOrdersCount?: number;
  siteCount?: number;
  orderCount?: number;
  bulkOrderCount?: number;
  deploymentCount?: number;
  plannedDevices?: number;
  installationInstructions?: InstallationInstructions;
  qualityChecklist?: string[];
  k2Customer?: string;
  expenses?: any[];
  smartsheetUrl?: string;
  opgNumber?: string;
  shippingLabel1?: string;
  shippingLabel2?: string;
  totalDevices?: number;
  enableCatalogPricing?: boolean;
  k2CustomerName?: string;
  customerFleet?: string;
  enableBulkOrdering?: boolean;
  customerPin?: string;
  siteNameStructure?: string;
  eotLeaseDate?: string;
  sourceName?: string;
  partnerPurchaseOrders?: any;
  rejected?: boolean;
  rejectedReason?: string;
  rejectedDate?: string;
  allowUnRejectProject?: boolean;
  holdDate?: string;
  estimatesIncrement?: ProjectOffHoldIncrementModel;
  orderAcceptanceCompleteDate?: string;
  legacyReportedDate?: string;
  multiYearStartDate?: string;
  multiYearEndDate?: string;
  geo_region_id?: string;
  allowSetGeoRegion: boolean;
  requireMaiPagecounts: boolean;
  smartsheetWorkspace: string;
  deliveryInformation?: string;
  isMigrationActive?: boolean;
}

function parseDateFromJSON(date) {
  if (!date) return null;
  const matches = `${date}`.match(/^([0-9]{4}-[0-9]{2}-[0-9]{2})T?/i);
  if (!matches) return null;
  return matches[1] || null;
}

function toNumber(obj): number {
  const f = parseFloat(obj);
  if (Number.isNaN(f)) return undefined;
  return f;
}

export function projectModelFactory(json?: any): ProjectModel {
  json = json || {};

  if (!json.id) {
    json.startDate = new Date().toISOString();
    json.status = "open";
  }

  if (json.country) {
    json.countryId = json.country.countryId;
    json.country.geoRegionId = json.country.geo_region_id;
  }

  if (json.dorData) {
    json.earlyOrderRec = json.dorData.earlyOrderRec;
    json.multiYear = json.dorData.multiYear;
    json.multiYearPoType = json.dorData.multiYearPoType;
    json.multiYearTerm = json.dorData.multiYearTerm;
    json.multiYearTermDuration = json.dorData.multiYearTermDuration;
    json.plant = json.dorData.plant;
    json.prepaidFieldOnDorTab = json.dorData.prepaidFieldOnDorTab;
    json.previousDocument = json.dorData.previousDocument;
    json.renewal = json.dorData.renewal;
    json.ruleDescription = json.dorData.ruleDescription;
    json.salesDocumentType = json.dorData.salesDocumentType;
    json.legacyReportedDate = json.dorData.legacyReportedDate;
    json.multiYearEndDate = json.dorData.multiYearEndDate;
    json.multiYearStartDate = json.dorData.multiYearStartDate;
    json.dorRelavant = json.dorData.dorRelavant;
    json.endCustomerSplit = json.dorData.endCustomerSplit;
    json.payerSplit = json.dorData.payerSplit;
    json.poNumberSplit = json.dorData.poNumberSplit;
    json.shipToSplit = json.dorData.shipToSplit;
    if (json.dorData.isMigrationActive) json.isMigrationActive = json.dorData.isMigrationActive;
  }

  return {
    id: json.id,
    name: json.name || "",
    countryId: json.countryId || "",
    country: json.country || {},
    status: json.status || "",
    resellerBpId: json.resellerBpId || "",
    resellerName: json.resellerName || "",
    customerBpId: json.customerBpId || "",
    customerName: json.customerName || "",
    customerFleet: json.customerFleet || "",
    uniqueId: json.uniqueId || "",
    targetSystem: json.targetSystem || "",
    opportunityId: json.opportunityId || "",
    startDate: parseDateFromJSON(json.startDate) || "",
    customerKickoffDate: parseDateFromJSON(json.customerKickoffDate) || "",
    estimatedProjectClosureDate: parseDateFromJSON(json.estimatedProjectClosureDate) || "",
    actualProjectClosureDate: parseDateFromJSON(json.actualProjectClosureDate) || "",
    estimatedTransitionDate: toNumber(json.estimatedTransitionDate) || 0,
    actualTransitionDate: toNumber(json.actualTransitionDate) || 0,
    estimatedCompletionDate: parseDateFromJSON(json.estimatedCompletionDate) || "",
    firstInstallDate: parseDateFromJSON(json.firstInstallDate) || "",
    firstOrderDate: parseDateFromJSON(json.firstOrderDate) || "",
    totalCustomerValue: toNumber(json.totalCustomerValue),
    currency: json.currency || "",
    users: userModelListFactory(json.users),
    ampId: json.ampId || "",
    csa: json.csa || "",
    msId: json.msId || "",
    orderAcceptance: !!json.orderAcceptance,
    orderAcceptanceDisableReason: json.orderAcceptanceDisableReason || "",
    orderAcceptanceDisableDate: json.orderAcceptanceDisableDate || "",
    sourceDocumentationLink: json.sourceDocumentationLink,
    initialBillingDate: json.initialBillingDate,
    billingStart: json.billingStart,
    dashboardMilestones: json.dashboardMilestones || [],
    dashboardIssues: json.dashboardIssues || [],
    customerErpNumber: json.customerErpNumber || "",
    handoverIdNumber: json.handoverIdNumber || "",
    address: addressModelFactory(json.address),
    billToContact: contactModelFactory(json.billToContact),
    workflowId: json.workflowId || "",
    deployedDevices: toNumber(json.deployedDevices),
    orderedDevices: toNumber(json.orderedDevices),
    requestedOrdersCount: toNumber(json.requestedOrdersCount),
    requestedBulkOrdersCount: toNumber(json.requestedBulkOrdersCount),
    siteCount: toNumber(json.siteCount),
    orderCount: toNumber(json.orderCount),
    bulkOrderCount: toNumber(json.bulkOrderCount),
    deploymentCount: toNumber(json.deploymentCount),
    plannedDevices: toNumber(json.plannedDevices),
    installationInstructions: installationInstructionsModelFactory(json.installationInstructions),
    qualityChecklist: json.qualityChecklist || [],
    k2Customer: json.k2Customer || "",
    expenses: json.expenses || [],
    smartsheetUrl: json.smartsheetUrl || "",
    opgNumber: json.opgNumber || "",
    shippingLabel1: json.shippingLabel1 || json.customShippingLabel1 || "",
    shippingLabel2: json.shippingLabel2 || json.customShippingLabel2 || "",
    totalDevices: toNumber(json.totalDevices),
    enableCatalogPricing: json.enableCatalogPricing || false,
    k2CustomerName: json.k2CustomerName || "",
    enableBulkOrdering: json.enableBulkOrdering || false,
    customerPin: json.customerPin || "",
    siteNameStructure: json.siteNameStructure || "",
    eotLeaseDate: parseDateFromJSON(json.eotLeaseDate) || "",
    sourceName: json.sourceName || "",
    partnerPurchaseOrders: json.partnerPurchaseOrders || [],
    rejected: !!json.rejected,
    rejectedReason: json.rejectedReason || "",
    rejectedDate: json.rejectedDate || "",
    allowUnRejectProject: !!json.allowUnRejectProject,
    holdDate: json.holdDate || "",
    estimatesIncrement: ProjectOffHoldIncrementModelFactory(json.estimatesIncrement),
    orderAcceptanceCompleteDate: parseDateFromJSON(json.orderAcceptanceCompleteDate) || "",
    legacyReportedDate: parseDateFromJSON(json.legacyReportedDate) || "",
    multiYearStartDate: parseDateFromJSON(json.multiYearStartDate) || "",
    multiYearEndDate: parseDateFromJSON(json.multiYearEndDate) || "",
    geo_region_id: json.geo_region_id || "", // eslint-disable-line @typescript-eslint/camelcase
    allowSetGeoRegion: !!json.allowSetGeoRegion,
    requireMaiPagecounts: !!json.requireMaiPagecounts,
    earlyOrderRec: json.earlyOrderRec || "",
    multiYear: json.multiYear || "",
    multiYearPoType: json.multiYearPoType || "",
    multiYearTerm: json.multiYearTerm || "",
    multiYearTermDuration: json.multiYearTermDuration || "",
    plant: json.plant || "",
    prepaidFieldOnDorTab: json.prepaidFieldOnDorTab || "",
    previousDocument: json.previousDocument || "",
    renewal: json.renewal || "",
    ruleDescription: json.ruleDescription || "",
    salesDocumentType: json.salesDocumentType || "",
    dorRelavant: json.dorRelavant || "",
    endCustomerSplit: json.endCustomerSplit || "",
    payerSplit: json.payerSplit || "",
    poNumberSplit: json.poNumberSplit || "",
    shipToSplit: json.shipToSplit || "",
    smartsheetWorkspace: json.smartsheetWorkspace || "",
    deliveryInformation: json.deliveryInformation || "",
    isMigrationActive: json.isMigrationActive || false,
  } as ProjectModel;
}

export function projectModelListFactory(json?: any) {
  return Array.from(json || []).map(project => projectModelFactory(project));
}

export const projectUpdateKeys = ["id",
  "name",
  "countryId",
  "status",
  "startDate",
  "resellerBpId",
  "resellerName",
  "customerBpId",
  "uniqueId",
  "targetSystem",
  "opportunityId",
  "customerKickoffDate",
  "estimatedProjectClosureDate",
  "actualProjectClosureDate",
  "estimatedCompletionDate",
  "totalCustomerValue",
  "currency",
  "sourceDocumentationLink",
  "initialBillingDate",
  "dashboardMilestones",
  "dashboardIssues",
  "installationInstructions",
  "qualityChecklist",
  "expenses",
  "smartsheetUrl",
  "customerPin",
  "eotLeaseDate",
  "sourceName",
  "partnerPurchaseOrders",
  "estimatesIncrement",
  "requireMaiPagecounts",
  "smartsheetWorkspace",
  "deliveryInformation",
];
